import { isInIframe } from "./isInIframe";

/**
 * Adopts the Shoptet admin theme when we run embedded in the admin (CZ3DES1-448).
 *
 * The admin only posts which body classes to hold — `iframe-section`, `theme-brand`, `theme-dark`.
 * Everything those classes switch lives in `@shoptet/ui`'s own stylesheet, which we already import,
 * so there is nothing else to load.
 *
 * The parent emits on our `load` event, whenever the user changes the setting, and whenever we ask.
 * A SPA has to ask: `load` fires once and never comes back on client-side navigation.
 *
 * The payload is applied statelessly — every entry is explicit, `false` included, so no memory of
 * earlier messages is needed and classes added later need no change here. No message at all means
 * the shop runs the stock design, which is why the classes have no default.
 *
 * Besides applying the classes, the theme is kept here for the few places that have to decide
 * something from it once rather than restyle with it — the Tally questionnaire picks its form by
 * it. Those also need to know whether the theme is settled yet: the first message arrives one
 * round trip after our request, but nothing orders it before our own API calls, and a stock-design
 * admin never sends one. So `known` flips on the first message, after a grace period without one,
 * or right away when there is no admin around us at all.
 */

const MESSAGE_TYPE = "shoptet:admin-theme";
const REQUEST_TYPE = "shoptet:admin-theme-request";
const DARK_CLASS = "theme-dark";

/** Generous for a postMessage round trip on a busy admin page, short enough not to hold up a modal. */
const REPLY_GRACE_MS = 1500;

export type AdminThemeT = {
  /** the admin's dark theme is on */
  dark: boolean;
  /** settled — by a message, by a grace period without one, or by there being no admin */
  known: boolean;
};

type AdminThemeMessage = {
  bodyClasses?: { [className: string]: boolean };
  type: string;
};

let theme: AdminThemeT = { dark: false, known: false };
const listeners = new Set<() => void>();

const settleTheme = () => {
  theme = { dark: document.body.classList.contains(DARK_CLASS), known: true };
  listeners.forEach((listener) => listener());
};

/** Snapshot for `useSyncExternalStore`: the same object until the theme settles or flips. */
export const getAdminTheme = (): AdminThemeT => theme;

export const subscribeToAdminTheme = (listener: () => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

const isAdminThemeMessage = (data: unknown): data is AdminThemeMessage =>
  typeof data === "object" && data !== null && (data as AdminThemeMessage).type === MESSAGE_TYPE;

export const applyAdminTheme = (bodyClasses: { [className: string]: boolean }) => {
  Object.entries(bodyClasses).forEach(([className, enabled]) => {
    document.body.classList.toggle(className, enabled);
  });
  settleTheme();

  // the theme changes our height, and iframe-resizer only learns about it if we say so
  window.parentIFrame?.reset();
};

const handleMessage = (event: MessageEvent) => {
  // only the page that embedded us gets to restyle us
  if (event.source !== window.parent || !isAdminThemeMessage(event.data)) {
    return;
  }

  applyAdminTheme(event.data.bodyClasses ?? {});
};

export const listenToAdminTheme = () => {
  if (!isInIframe()) {
    // nothing will restyle us but the dev switcher, and that goes through `applyAdminTheme` too
    settleTheme();
    return;
  }

  window.addEventListener("message", handleMessage);
  window.parent.postMessage({ type: REQUEST_TYPE }, "*");

  // a stock-design admin never answers, and the classes it leaves alone are the theme it runs
  window.setTimeout(() => {
    if (!theme.known) {
      settleTheme();
    }
  }, REPLY_GRACE_MS);
};
